import React, { useState, useEffect } from 'react';
import { CustomTooltip } from 'app/components/Common/Tooltips';
import { TypeAheadDropdown } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/TypeAheadDropdown';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'oidc/user.selectors';
import { selectPlacementHeaderData, selectPlacementTags } from 'store/redux-store/placement-details/selectors';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { IconButton, useTheme } from '@mui/material';
import { TagValues } from './PlacementTagsWrapper';
import { canManageOgpConfirmedStartTag, isCandidateContractLeadUser, useReadOnly } from 'oidc/userRoles';
import { useDecision } from '@optimizely/react-sdk';
import { ff_placement_ui_arbitration_agreement_grids } from 'app/constants/FeatureFlags/Global/keys';
import PersonalTags from './PersonalTags';

export const AddTagsIcon = ({
  anchorAccessorId,
  tagsOptions,
  tagsList,
  selectedTagId,
  setSelectedTagId,
}: {
  anchorAccessorId: string;
  tagsOptions;
  tagsList: any[];
  selectedTagId: number;
  setSelectedTagId: (tagId: number) => void;
}) => {
  const { t } = useTranslation();
  const anchorRef = React.useRef<any>(null);
  const dispatch = useDispatch();
  const placementDetails = useSelector(selectPlacementHeaderData);
  const user = useSelector(selectUser);
  const theme = useTheme();
  const [arbitrationAgreementFlag] = useDecision(ff_placement_ui_arbitration_agreement_grids);

  const [selectedTags, setSelectedTags] = useState([]);
  const { placementFlagTypeId } = useSelector(selectPlacementTags);
  const { readOnly } = useReadOnly();

  useEffect(() => {
    setSelectedTags(
      tagsList.filter(tag => {
        if (tag.readOnly) return false;
        if (tag.label === TagValues.TLA && !isCandidateContractLeadUser(user.userInfo.roles)) return false;
        if (tag.label === TagValues.OGP_CONFIRMED_START && !canManageOgpConfirmedStartTag(user.userInfo.roles)) {
          return false;
        }
        return true;
      }),
    );
  }, [tagsList, user?.userInfo?.roles]);

  const handleOpen = () => {
    const btn = document.getElementById(anchorAccessorId);
    btn?.click();
    setSelectedTagId(placementFlagTypeId);
  };

  const handleApply = () => {
    const readyOnlyTags = tagsList?.filter(
      tag => tag.readOnly || checkTLA(tag.label) || checkOgpConfirmedStart(tag.label),
    );
    const updatedTags = {
      tags: selectedTags.concat(readyOnlyTags),
      placementFlagTypeId: selectedTagId,
    }
    const postReqBody = {
      placementId: placementDetails?.placementId,
      updatedBy: user.userInfo.employeeId,
      placementTags: updatedTags,
      arbitrationAgreementFlagEnabled: arbitrationAgreementFlag?.enabled,
    };

    dispatch(placementDetailsAction.updatePlacementTags(postReqBody));
  };

  const checkTLA = (label: string): boolean => {
    if (label === TagValues.TLA) {
      return !isCandidateContractLeadUser(user.userInfo.roles);
    }
    return false;
  };

  const checkOgpConfirmedStart = (label: string): boolean => {
    if (label === TagValues.OGP_CONFIRMED_START) {
      return !canManageOgpConfirmedStartTag(user.userInfo.roles);
    }
    return false;
  };

  const onColorTagSelect = (tagId: number) => {
    setSelectedTagId(tagId);
  };

  const getOptionDisabled = (option: any) => {
    // Disable options that have isDisabled: true
    return option.isDisabled === true;
  };

  const getOptionTooltip = (option: any) => {
    // Return tooltip text for disabled options
    if (option.isDisabled && option.disabledTooltip) {
      return option.disabledTooltip;
    }
    return null;
  };

  return (
    <div style={{ width: '35px', height: '32px' }}>
      <CustomTooltip
        style={{ color: theme.palette.system.neutralGray, top: 5 }}
        tooltipContent={t('placement.profile.header.addTags')}
        placement="bottom-start"
        isInlineContent={true}
        tooltipPlacementBottom={true}
      >
        <IconButton
          onClick={e => {
            handleOpen();
            e.stopPropagation();
          }}
          color="secondary"
          disabled = {readOnly}
          size="medium"
          ref={anchorRef}
          id="add-icon-tags"
          sx={{
            height: '32px',
            width: '32px',
            marginLeft: theme.spacing(),
            // '&:hover': {
            //   color: theme.palette.secondary.main,
            // },
          }}
        >
          <AddIcon />
        </IconButton>
      </CustomTooltip>
      <TypeAheadDropdown
        name="tags-type-ahead-control"
        value={selectedTags}
        anchorRef={anchorRef}
        onChange={newValue => setSelectedTags(newValue)}
        options={tagsOptions ?? []}
        isMultiSelect
        anchorAccesorid={anchorAccessorId}
        apply={handleApply}
        showListBox
        customList={<PersonalTags onTagSelect={onColorTagSelect} selecetedTagId={selectedTagId} />}
        getOptionDisabled={getOptionDisabled}
        getOptionTooltip={getOptionTooltip}
      />
    </div>
  );
};
